import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Staveniště - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: NV 591/2006 Sb. (bližší minimální požadavky na BOZP na
 * staveništích); zákon 309/2006 Sb. §14-15 (koordinátor BOZP, plán BOZP);
 * zákoník práce §101(3) (vzájemné informování zhotovitelů). Bilingual
 * (cs-CZ + en-US). Three chapters (vstup | pohyb | více zhotovitelů) + a
 * graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const constructionSiteProfile: TrainingProfile = {
  slug: 'construction-site',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Práce na staveništi',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-vstup',
        title: t('Vstup na staveniště', 'Entering a construction site'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-vstup',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Staveniště má vlastní pravidla', 'A construction site has its own rules'),
          },
          {
            id: 'p-vstup-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Jakmile tvá zakázka vede na **aktivní staveniště**, přestáváš být „řemeslník u zákazníka" a stáváš se **zhotovitelem na staveništi** - platí pro tebe **vstupní instruktáž**, pokyny **koordinátora BOZP** a **plán BOZP** stavby. Bez instruktáže na staveniště nevstupuj.',
              "The moment your job leads onto an **active construction site**, you stop being \"a tradesperson at a customer's\" and become **a contractor on site** - the **site induction**, the **OSH coordinator's instructions** and the site's **OSH plan** apply to you. Do not enter without the induction.",
            ),
          },
          {
            id: 'anim-vstup',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'siteInduction',
            steps: [
              {
                label: t('Přilba a vesta', 'Helmet and hi-vis'),
                say: t('Na staveniště jen v OOPP.', 'Enter the site only in PPE.'),
              },
              {
                label: t('Zápis do evidence', 'Sign the register'),
                say: t('Evidence osob na staveništi.', 'The site register of persons.'),
              },
              {
                label: t('Instruktáž a plán BOZP', 'Induction and OSH plan'),
                say: t(
                  'Seznam se s plánem BOZP stavby a jeho riziky.',
                  'Read the site OSH plan and its risks.',
                ),
              },
            ],
            caption: t(
              'Vstup na staveniště: přilba a vesta, zápis do knihy návštěv, seznámení s riziky.',
              'Entering the site: helmet and hi-vis, sign the visitor log, get briefed on the risks.',
            ),
          },
          {
            id: 'c-vstup-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 591/2006 Sb.** (požadavky na BOZP na staveništích) a **zákon 309/2006 Sb. §14-15** (koordinátor BOZP a plán BOZP).',
              'Legal basis: **Gov. Reg. 591/2006 Coll.** (OSH requirements on construction sites) and **Act 309/2006 Coll. §14-15** (OSH coordinator and OSH plan).',
            ),
          },
          {
            id: 'cl-vstup',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Přilba, reflexní vesta, pevná obuv (S3)',
                'Helmet, hi-vis vest, safety boots (S3)',
              ),
              t(
                'Zápis do knihy návštěv / evidence osob',
                'Signed into the visitor log / site register',
              ),
              t('Absolvovaná vstupní instruktáž', 'Site induction completed'),
              t(
                'Vím, kdo je koordinátor BOZP a stavbyvedoucí',
                'I know who the OSH coordinator and site manager are',
              ),
            ],
          },
        ],
      },
      {
        id: 'ch-pohyb',
        title: t('Pohyb po staveništi', 'Moving around the site'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-jerab'],
        blocks: [
          {
            id: 'h-pohyb',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Kudy ano, kudy ne', 'Where to walk - and where not to'),
          },
          {
            id: 'p-pohyb-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Po staveništi se pohybuj **jen po vyznačených komunikacích**. Nikdy nevstupuj **pod zavěšené břemeno** ani do **nezapaženého výkopu**; výkopy hlubší než 1,3 m v zastavěném území musí být **zapažené** a přechody přes ně zajištěné lávkou se zábradlím.',
              'Move **only along the marked routes**. Never step **under a suspended load** or into an **unshored excavation**; trenches deeper than 1.3 m in built-up areas must be **shored**, with guarded crossings.',
            ),
          },
          {
            id: 'kc-jerab',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Jeřáb přenáší paletu přes tvou trasu. Co uděláš?',
              'A crane is carrying a pallet across your route. What do you do?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Projdu rychle, náklad je vysoko',
                  'Walk through quickly, the load is high up',
                ),
              },
              {
                id: 'kc-b',
                label: t('Počkám mimo dráhu břemene', "Wait clear of the load's path"),
              },
              {
                id: 'kc-c',
                label: t(
                  'Proběhnu, když se nedívá jeřábník',
                  "Run through when the crane operator isn't looking",
                ),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Pod zavěšeným břemenem se nikdy neprochází - pád břemene patří k nejtěžším staveništním úrazům.',
              'Never pass under a suspended load - falling loads cause some of the most severe site injuries.',
            ),
          },
          {
            id: 'c-pohyb-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Udržuj pořádek: kabely a hadice veď tak, ať nevytvářejí **místa zakopnutí**; materiál skladuj stabilně a mimo únikové cesty.',
              "Keep the workplace tidy: route cables and hoses so they don't create **trip points**; store materials stably and clear of escape routes.",
            ),
          },
        ],
      },
      {
        id: 'ch-zhotovitele',
        title: t('Více zhotovitelů na jedné stavbě', 'Multiple contractors on one site'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-rizika'],
        blocks: [
          {
            id: 'h-zhotovitele',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Tvá rizika nejsou jen tvoje', 'Your risks are not only yours'),
          },
          {
            id: 'p-zhotovitele-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Pracuje-li na stavbě víc firem, musí se **vzájemně písemně informovat o rizicích** (zákoník práce §101(3)). Nikdy **nezasahuj do cizích konstrukcí** - lešení, pažení nebo zábradlí smí upravit jen ten, kdo je postavil. Zjištěné závady hlas stavbyvedoucímu.',
              "When several companies share a site, they must **inform each other about risks in writing** (Labour Code §101(3)). Never **modify another contractor's structures** - scaffolds, shoring or railings may only be altered by whoever erected them. Report defects to the site manager.",
            ),
          },
          {
            id: 'ord-rizika',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď postup, když objevíš odstraněné zábradlí u schodiště.',
              'Order the steps when you find a removed stair railing.',
            ),
            items: [
              {
                id: 'or-1',
                label: t(
                  'Nepřibližuj se k nezajištěné hraně',
                  'Keep clear of the unprotected edge',
                ),
              },
              {
                id: 'or-2',
                label: t(
                  'Ohlas závadu stavbyvedoucímu / koordinátorovi',
                  'Report it to the site manager / coordinator',
                ),
              },
              { id: 'or-3', label: t('Varuj ostatní v okolí', 'Warn others nearby') },
              {
                id: 'or-4',
                label: t('Pokračuj v práci až po zajištění', 'Resume work only once it is secured'),
              },
            ],
          },
          {
            id: 'k-zhotovitele',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Na staveništi platí plán BOZP a pokyny koordinátora. **Cizí konstrukce neupravuj, závady hlas, pod břemena nevstupuj.**',
              "On site, the OSH plan and the coordinator's instructions rule. **Don't modify others' structures, report defects, never walk under loads.**",
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-vstup-single',
      kind: 'single',
      chapterId: 'ch-vstup',
      prompt: t(
        'Co musíš absolvovat před první prací na aktivním staveništi?',
        'What must you complete before first working on an active site?',
      ),
      choices: [
        { id: 'a', label: t('Vstupní instruktáž staveniště', 'The site induction') },
        { id: 'b', label: t('Stačí podpis smlouvy', 'Signing the contract is enough') },
        {
          id: 'c',
          label: t('Nic, mám vlastní školení BOZP', 'Nothing, I have my own OSH training'),
        },
      ],
      correct: 'a',
    },
    {
      id: 'q-bremeno-tf',
      kind: 'truefalse',
      chapterId: 'ch-pohyb',
      prompt: t(
        'Pod zavěšeným břemenem lze projít, pokud jde o krátkou vzdálenost.',
        'You may pass under a suspended load if it is only a short distance.',
      ),
      correct: false,
    },
    {
      id: 'q-vstup-multi',
      kind: 'multi',
      chapterId: 'ch-vstup',
      prompt: t(
        'Co patří k povinné výbavě při vstupu na staveniště? (vyber všechny správné)',
        'What belongs to mandatory kit when entering a site? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Přilba', 'Helmet') },
        { id: 'b', label: t('Reflexní vesta', 'Hi-vis vest') },
        { id: 'c', label: t('Sluneční brýle', 'Sunglasses') },
        { id: 'd', label: t('Pevná pracovní obuv', 'Safety boots') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-vykop-numeric',
      kind: 'numeric',
      chapterId: 'ch-pohyb',
      prompt: t(
        'Od jaké hloubky (v metrech) musí být výkop v zastavěném území zapažen?',
        'From what depth (in metres) must a trench in a built-up area be shored?',
      ),
      correct: 1.3,
      tolerance: 0,
    },
    {
      id: 'q-zavada-order',
      kind: 'ordering',
      chapterId: 'ch-zhotovitele',
      prompt: t(
        'Seřaď reakci na zjištěnou závadu (chybějící zábradlí).',
        'Order the response to a found defect (missing railing).',
      ),
      choices: [
        { id: 'a', label: t('Držet se od hrany', 'Keep clear of the edge') },
        { id: 'b', label: t('Varovat ostatní', 'Warn others') },
        { id: 'c', label: t('Ohlásit stavbyvedoucímu', 'Report to the site manager') },
        { id: 'd', label: t('Pokračovat po zajištění', 'Resume once secured') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
